<template>
  <aside
    class="explore-sidebar"
    :class="{ collapsed: internalCollapsed }"
    role="complementary"
  >
    <header class="explore-sidebar__header">
      <button
        class="explore-sidebar__toggle"
        type="button"
        aria-label="Hide sidebar"
        @click="collapsePanel"
      >
        <span class="material-icons">left_panel_close</span>
      </button>
    </header>
    <div class="explore-sidebar__content">
      <ExploreV2TrailListSideBar />
    </div>
  </aside>

  <button
    v-if="internalCollapsed && !dropdownActive"
    class="explore-sidebar__expand"
    type="button"
    aria-label="Show sidebar"
    @click="expandPanel"
  >
    <span class="material-icons">left_panel_open</span>
    <span>{{ expandLabel }}</span>
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ExploreV2TrailListSideBar from './ExploreV2TrailListSideBar.vue';

export default defineComponent({
  components: {
    ExploreV2TrailListSideBar,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:collapsed'],
  data() {
    return {
      isMobile: window.innerWidth <= 600,
      internalCollapsed: false,
      dropdownActive: false,
    };
  },
  watch: {
    collapsed: {
      immediate: true,
      handler(value: boolean) {
        this.internalCollapsed = value;
      },
    },
  },
  computed: {
    expandLabel() {
      const source = this.$store?.state?.exploreV2?.searchSource || 'hikes';
      return source === 'community' ? 'Community' : 'Hikes';
    },
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 600;
      if (this.isMobile && !this.internalCollapsed) {
        this.internalCollapsed = true;
        this.$emit('update:collapsed', true);
      }
    },
    collapsePanel() {
      if (!this.internalCollapsed) {
        this.internalCollapsed = true;
        this.$emit('update:collapsed', true);
      }
    },
    expandPanel() {
      if (this.internalCollapsed) {
        this.internalCollapsed = false;
        this.$emit('update:collapsed', false);
      }
    },
    onDropdownActive(e: CustomEvent) {
      this.dropdownActive = e.detail;
    },
  },
  mounted() {
    this.checkMobile();
    this.internalCollapsed = this.collapsed || this.isMobile;
    if (this.internalCollapsed !== this.collapsed) {
      this.$emit('update:collapsed', this.internalCollapsed);
    }
    window.addEventListener('resize', this.checkMobile);
    window.addEventListener('explore-dropdown-active', this.onDropdownActive);
  },
  unmounted() {
    window.removeEventListener('resize', this.checkMobile);
    window.removeEventListener('explore-dropdown-active', this.onDropdownActive);
  },
});
</script>

<style scoped>
.explore-sidebar {
  position: absolute;
  top: 1rem;
  left: 1rem;
  --explore-sidebar-width: 320px;
  width: var(--explore-sidebar-width);
  max-width: 88vw;
  height: calc(100vh - 2rem);
  max-height: 85vh;
  background: rgba(27, 27, 29, 0.92);
  color: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(10px);
  z-index: 500;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.explore-sidebar.collapsed {
  transform: translateX(-120%);
  opacity: 0;
  pointer-events: none;
}

.explore-sidebar__header {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 0.75rem 0 0.75rem;
}

.explore-sidebar__toggle {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 6px;
  line-height: 0;
  transition: background 0.2s ease, color 0.2s ease;
}

.explore-sidebar__toggle:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--yellow);
}

.explore-sidebar__content {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 0.75rem 0.75rem;
}

.explore-sidebar__expand {
  width: auto;
  padding: .5em 1em;
  height: 32px;
  position: absolute;
  top: 6rem;
  left: 1rem;
  background: rgba(27, 27, 29, 0.92);
  color: #fff;
  border: none;
  border-radius: 100px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  z-index: 550;
  backdrop-filter: blur(8px);
  transition: background 0.2s ease;
}

.explore-sidebar__expand:hover {
  background: rgba(43, 43, 49, 0.95);
}

@media (max-width: 600px) {
  .explore-sidebar {
    width: min(420px, 92vw);
    left: 0.75rem;
  }

  .explore-sidebar__expand {
    width: 32px;
    left: 0.75rem;
    top: 6rem;
  }
}

@media (max-width: 600px) {
  .explore-sidebar {
    width: calc(100vw - 1.5rem);
    left: 0.75rem;
  }

  .explore-sidebar__expand span:last-child {
    display: none;
  }

  .explore-sidebar__expand {
    top: 5.5rem;
  }
}
</style>
